import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Unity } from 'react-unity-webgl';
import { useCharacter } from '../../context/CharacterContext';
import { useUser } from '../../context/UserContext';
import { useUnityCharacterPreview } from '@gamedate/core-character/useUnityCharacterPreview';
import { UNITY_BUILD_PATHS } from '../../config/unityBuild';
import {
  AppearanceSettings,
  ColorRGBA,
  DEFAULT_APPEARANCE,
  EYE_COLOR_PRESETS,
  HAIR_COLOR_PRESETS,
  MORPH_SPECS,
  MorphKey,
  OUTFIT_COUNT,
  PART_CATALOG,
  PART_FIELD_MAP,
  PartSlot,
  SKIN_PRESETS,
  parseAppearanceData,
  serializeAppearanceData,
} from '@gamedate/core-character/appearance';
import { Character, CharacterClass, Gender } from '@gamedate/core-character/character';
import { Gender as UserGender } from '../../types/user';
import { CharacterSlotSelector } from './CharacterSlotSelector';
import { emit as emitTelemetry, newCorrelationId, nowMs } from '../../services/telemetry/FlexoTelemetry';

// Convert user gender ('man'/'woman'/etc) to character gender ('male'/'female')
function userGenderToCharacterGender(userGender: UserGender | undefined): Gender {
  if (userGender === 'woman' || userGender === 'female') return 'female';
  return 'male';
}
import { GenderSelector } from '@gamedate/core-character/controls/GenderSelector';
import { ColorSwatchPicker } from '@gamedate/core-character/controls/ColorSwatchPicker';
import { PartImageGrid } from '@gamedate/core-character/controls/PartImageGrid';
import { MorphSlider } from '@gamedate/core-character/controls/MorphSlider';
import {
  PresetIcon, BodyIcon, FaceIcon, HairIcon, OutfitIcon, AccessoriesIcon, GesturesIcon,
  StatureIcon, BuildIcon, LimbsIcon, FrameIcon, PiecesIcon,
  DiceIcon, ResetIcon, TrashIcon, BackIcon, CheckIcon, AlertIcon,
} from '@gamedate/core-character/controls/EditorIcons';
import {
  PICKABLE_GESTURES,
  GESTURE_CATEGORY_LABELS,
  MAX_GESTURE_SLOTS,
  DEFAULT_GESTURE_LOADOUT,
} from '@gamedate/core-character/gestures';
import './CharacterEditorInline.css';

type EditorTab = 'preset' | 'body' | 'face' | 'hair' | 'outfit' | 'accessories' | 'gestures';

const EDITOR_TABS: { id: EditorTab; label: string; Icon: (p: { size?: number }) => JSX.Element }[] = [
  { id: 'preset', label: 'Preset', Icon: PresetIcon },
  { id: 'body', label: 'Body', Icon: BodyIcon },
  { id: 'face', label: 'Face', Icon: FaceIcon },
  { id: 'hair', label: 'Hair', Icon: HairIcon },
  { id: 'outfit', label: 'Outfit', Icon: OutfitIcon },
  { id: 'accessories', label: 'Accessories', Icon: AccessoriesIcon },
  { id: 'gestures', label: 'Gestures', Icon: GesturesIcon },
];

const OUTFIT_LABELS = ['Base', 'Casual', 'Urban', 'Business', 'Athletic', 'Streetwear', 'Formal'];

// Body tab: clustered sliders with an icon header per cluster — a visual map
// of the body, not a flat parameter list.
const BODY_GROUPS: { title: string; Icon: (p: { size?: number }) => JSX.Element; keys: MorphKey[] }[] = [
  { title: 'Stature', Icon: StatureIcon, keys: ['height', 'bodyScale'] },
  { title: 'Build', Icon: BuildIcon, keys: ['muscleValue', 'buildValue', 'feminineValue'] },
  { title: 'Limbs', Icon: LimbsIcon, keys: ['legLength', 'armLength', 'thighSize', 'bicepsSize'] },
  { title: 'Frame', Icon: FrameIcon, keys: ['shoulderWidth', 'torsoWidth', 'headSize'] },
];

const ANIMATION_EMOJI: Record<string, string> = {
  idle: '\u{1F9CD}', // 🧍
  walk: '\u{1F6B6}', // 🚶
  run: '\u{1F3C3}', // 🏃
  wave: '\u{1F44B}', // 👋
  dance: '\u{1F57A}', // 🕺
};

// Random helpers for the Preset tab's Randomize button
const randomIntInclusive = (maxInclusive: number) =>
  Math.floor(Math.random() * (maxInclusive + 1));
const randomPresetColor = (presets: { color: ColorRGBA; label: string }[]): ColorRGBA =>
  presets[Math.floor(Math.random() * presets.length)].color;

export function CharacterEditorInline() {
  const { user, updateProfile } = useUser();
  const {
    characters,
    activeCharacter,
    error: contextError,
    createCharacter,
    updateCharacter,
    deleteCharacter,
  } = useCharacter();

  // Editor state
  const [mode, setMode] = useState<'select' | 'create' | 'edit'>('select');
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<EditorTab>('preset');

  // Form state
  const [name, setName] = useState('');
  const [gender, setGender] = useState<Gender>('male');
  const [characterClass, setCharacterClass] = useState<CharacterClass>('tank');
  const [appearance, setAppearance] = useState<AppearanceSettings>(DEFAULT_APPEARANCE);

  // Track if there are unsaved changes
  const [hasChanges, setHasChanges] = useState(false);

  // One correlation id threads this editor session's funnel telemetry (restore -> save).
  const funnelCidRef = useMemo(() => newCorrelationId(), []);
  // editor_state_restored fires once per mount when a returning user's state is restored.
  const restoredEmittedRef = useRef(false);

  const hasLiveToken = () =>
    typeof localStorage !== 'undefined' && !!localStorage.getItem('gamedate_token');

  // Unity preview hook - using centralized config from unityBuild.ts
  const unityConfig = useMemo(
    () => ({
      loaderUrl: UNITY_BUILD_PATHS.loaderUrl,
      dataUrl: UNITY_BUILD_PATHS.dataUrl,
      frameworkUrl: UNITY_BUILD_PATHS.frameworkUrl,
      codeUrl: UNITY_BUILD_PATHS.codeUrl,
      // Addressables catalog root — remote gestures fail without it (P2 A1).
      streamingAssetsUrl: UNITY_BUILD_PATHS.streamingAssetsUrl,
    }),
    []
  );

  const {
    unityProvider,
    isLoaded: isUnityLoaded,
    isReady: isUnityReady,
    loadingProgression,
    setGender: setUnityGender,
    setOutfit: setUnityOutfit,
    setHat: setUnityHat,
    setGlasses: setUnityGlasses,
    setBackpack: setUnityBackpack,
    setFacialHair: setUnityFacialHair,
    setPart,
    setMorph,
    setCameraView,
    setPreviewRotation,
    updateSkinColor,
    updateHairStyle,
    updateHairColor,
    updateEyeColor,
    loadFullAppearance,
    setCharacterType,
    playAnimation,
  } = useUnityCharacterPreview(unityConfig);

  // Animation state
  const [currentAnimation, setCurrentAnimation] = useState<string>('walk');

  // Canvas DPR (B12): render the Unity canvas at the device pixel ratio so it
  // is crisp on retina/mobile. Captured once by design: react-unity-webgl
  // 9.9.0 memoizes the Unity arguments at instance creation
  // (use-unity-arguments.js: useMemo(..., [])), so post-mount changes to the
  // devicePixelRatio prop never reach Unity. Live DPR tracking (moving the
  // window between screens, zoom) would need a library upgrade or manual
  // canvas backing-store resizing — not instance recreation.
  const devicePixelRatio = useMemo(
    () => (typeof window !== 'undefined' ? window.devicePixelRatio : 1),
    []
  );

  // Initialize from active character
  useEffect(() => {
    if (activeCharacter && mode === 'select') {
      setSelectedCharacter(activeCharacter);
      loadCharacterIntoForm(activeCharacter);
      setMode('edit');
    }
  }, [activeCharacter]);

  // US-7 AC-1 tripwire: a returning user's character state was restored into the editor.
  // Emit once per mount, when the characters list has resolved.
  useEffect(() => {
    if (restoredEmittedRef.current || characters.length === 0) return;
    restoredEmittedRef.current = true;
    emitTelemetry({
      event: 'editor_state_restored',
      roomId: null, playerId: user?.id ?? null, correlationId: funnelCidRef, ts: nowMs(),
      payload: { characterCount: characters.length, hadActiveSelection: !!activeCharacter },
    });
  }, [characters.length, activeCharacter, user?.id, funnelCidRef]);

  // Latest appearance, readable by the load/identity effect below without
  // re-firing it on every edit. Declared before that effect so the ref is
  // fresh when both run in the same commit (e.g. a character switch).
  const appearanceRef = useRef(appearance);
  useEffect(() => {
    appearanceRef.current = appearance;
  }, [appearance]);

  // Push the FULL character to Unity only on load/identity events: the
  // preview becoming ready, entering create/edit, or a gender/class change
  // (which rebuilds the rig). Per-edit changes ride the granular
  // setPart/setMorph/color setters instead — keying this effect on
  // `appearance` used to send an unthrottled LoadFullAppearance full
  // reassembly per slider tick during Body-tab drags, defeating the 50ms
  // per-key SetMorph throttle (design §6.2). Character switches, cancel
  // reverts, Randomize and Reset send their own explicit loadFullAppearance.
  useEffect(() => {
    if (isUnityReady && (mode === 'create' || mode === 'edit')) {
      setCharacterType(characterClass, gender);
      loadFullAppearance(appearanceRef.current);
    }
  }, [isUnityReady, mode, characterClass, gender, setCharacterType, loadFullAppearance]);

  // Start walk animation when Unity is ready
  useEffect(() => {
    if (isUnityReady) {
      playAnimation('walk');
    }
  }, [isUnityReady, playAnimation]);

  // Frame the head while on the Face tab; back to full body everywhere else.
  useEffect(() => {
    if (!isUnityReady) return;
    setCameraView(activeTab === 'face' ? 'face' : 'body');
  }, [activeTab, isUnityReady, setCameraView]);

  // Drag-to-rotate the preview (pointer events cover mouse AND touch)
  const rotationRef = useRef(0);
  const dragStateRef = useRef<{ pointerId: number; lastX: number } | null>(null);

  const handlePreviewPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragStateRef.current = { pointerId: e.pointerId, lastX: e.clientX };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const handlePreviewPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragStateRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const deltaX = e.clientX - drag.lastX;
      if (deltaX === 0) return;
      drag.lastX = e.clientX;
      rotationRef.current = (rotationRef.current + deltaX * 0.6 + 360) % 360;
      if (isUnityReady) {
        setPreviewRotation(rotationRef.current);
      }
    },
    [isUnityReady, setPreviewRotation]
  );

  const handlePreviewPointerEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current?.pointerId === e.pointerId) {
      dragStateRef.current = null;
    }
  }, []);

  // Handle animation button click
  const handleAnimationChange = useCallback(
    (anim: string) => {
      setCurrentAnimation(anim);
      playAnimation(anim);
    },
    [playAnimation]
  );

  // Send individual updates to Unity for real-time preview.
  // Gender is not just metadata: it drives the body silhouette AND the movement
  // set (Unity plays the feminine locomotion clips when feminineValue ≥ 0.5).
  // Female also clears facial hair. The Femininity slider stays available for
  // fine-tuning afterwards.
  const handleGenderChange = useCallback(
    (newGender: Gender) => {
      setGender(newGender);
      setHasChanges(true);
      if (isUnityReady) {
        setUnityGender(newGender);
      }
      const feminine = newGender === 'female' ? 1 : 0;
      setAppearance((prev) => ({
        ...prev,
        feminineValue: feminine,
        ...(newGender === 'female' ? { facialHairStyle: 0 } : {}),
      }));
      if (isUnityReady) {
        setMorph('feminineValue', feminine);
        if (newGender === 'female') setUnityFacialHair(0);
      }
    },
    [isUnityReady, setUnityGender, setMorph, setUnityFacialHair]
  );

  const handleAppearanceChange = useCallback(
    (updates: Partial<AppearanceSettings>) => {
      setAppearance((prev) => {
        const newAppearance = { ...prev, ...updates };

        // Send individual updates to Unity
        if (isUnityReady) {
          if (updates.skinColor) updateSkinColor(updates.skinColor);
          if (updates.hairStyle !== undefined) updateHairStyle(updates.hairStyle);
          if (updates.hairColor) updateHairColor(updates.hairColor);
          if (updates.eyeColor) updateEyeColor(updates.eyeColor);
          if (updates.outfitIndex !== undefined) setUnityOutfit(updates.outfitIndex);
          if (updates.facialHairStyle !== undefined) setUnityFacialHair(updates.facialHairStyle);
          if (updates.hatIndex !== undefined) setUnityHat(updates.hatIndex);
          if (updates.glassesIndex !== undefined) setUnityGlasses(updates.glassesIndex);
          if (updates.backpackIndex !== undefined) setUnityBackpack(updates.backpackIndex);
        }

        return newAppearance;
      });
      setHasChanges(true);
    },
    [isUnityReady, updateSkinColor, updateHairStyle, updateHairColor, updateEyeColor,
     setUnityOutfit, setUnityFacialHair, setUnityHat, setUnityGlasses, setUnityBackpack]
  );

  // Per-slot part override (§3.2). Legacy slots keep their dedicated bridge
  // messages; everything new rides the granular SetPart channel.
  const handlePartChange = useCallback(
    (slot: PartSlot, index: number) => {
      const field = PART_FIELD_MAP[slot];
      setAppearance((prev) => ({ ...prev, [field]: index }));
      setHasChanges(true);
      if (!isUnityReady) return;
      switch (slot) {
        case 'hair':
          updateHairStyle(index);
          break;
        case 'facialHair':
          setUnityFacialHair(index);
          break;
        case 'hat':
          setUnityHat(index);
          break;
        case 'glasses':
          setUnityGlasses(index);
          break;
        case 'backpack':
          setUnityBackpack(index);
          break;
        default:
          setPart(slot, index);
      }
    },
    [isUnityReady, updateHairStyle, setUnityFacialHair, setUnityHat, setUnityGlasses,
     setUnityBackpack, setPart]
  );

  // Body morph slider change — setMorph is throttled per key inside the hook.
  const handleMorphChange = useCallback(
    (key: MorphKey, value: number) => {
      setAppearance((prev) => ({ ...prev, [key]: value }));
      setHasChanges(true);
      if (isUnityReady) {
        setMorph(key, value);
      }
    },
    [isUnityReady, setMorph]
  );

  // Gesture loadout: toggle a gesture in/out of the (max 8) loadout. Newly
  // picked gestures preview immediately on the Unity character; tapping an
  // already-full grid still previews without selecting.
  const handleGestureToggle = useCallback(
    (id: string) => {
      const current = appearanceRef.current.gestures ?? [];
      const isSelected = current.includes(id);
      if (!isSelected && current.length >= MAX_GESTURE_SLOTS) {
        if (isUnityReady) playAnimation(id);
        return;
      }
      const next = isSelected ? current.filter((g) => g !== id) : [...current, id];
      setAppearance((prev) => ({ ...prev, gestures: next }));
      setHasChanges(true);
      if (!isSelected && isUnityReady) {
        playAnimation(id);
      }
    },
    [isUnityReady, playAnimation]
  );

  const handleGestureReset = useCallback(() => {
    setAppearance((prev) => ({ ...prev, gestures: [...DEFAULT_GESTURE_LOADOUT] }));
    setHasChanges(true);
  }, []);

  // Randomize everything except the character's name and skin tone (design
  // §6.3: "keep skin/name"): parts incl. 0/auto, morphs within their ranges,
  // hair/eye colors from the preset palettes.
  const handleRandomize = useCallback(() => {
    const next: AppearanceSettings = { ...appearanceRef.current };
    next.outfitIndex = 1 + Math.floor(Math.random() * OUTFIT_COUNT);
    (Object.keys(PART_CATALOG) as PartSlot[]).forEach((slot) => {
      const count = PART_CATALOG[slot];
      // Hair has no auto/none slot — every value 0..count-1 is a real style.
      next[PART_FIELD_MAP[slot]] = slot === 'hair'
        ? randomIntInclusive(count - 1)
        : randomIntInclusive(count);
    });
    MORPH_SPECS.forEach((spec) => {
      const value = spec.min + Math.random() * (spec.max - spec.min);
      next[spec.key] = Math.round(value * 100) / 100;
    });
    // Respect the selected gender: keep the silhouette on its side of the 0.5
    // feminineValue threshold (it also gates the feminine movement set in Unity),
    // and no beards on female characters.
    next.feminineValue = gender === 'female'
      ? Math.round((0.6 + Math.random() * 0.4) * 100) / 100
      : Math.round(Math.random() * 0.4 * 100) / 100;
    if (gender === 'female') next.facialHairStyle = 0;
    next.backpackIndex = 0; // backpacks disabled product-wide (face-placement defect)
    next.hairColor = randomPresetColor(HAIR_COLOR_PRESETS);
    next.eyeColor = randomPresetColor(EYE_COLOR_PRESETS);
    setAppearance(next);
    setHasChanges(true);
    // The load/identity effect no longer keys on appearance — send the one
    // full snapshot this discrete action needs.
    if (isUnityReady) {
      loadFullAppearance(next);
    }
  }, [isUnityReady, loadFullAppearance, gender]);

  const handleResetAppearance = useCallback(() => {
    setAppearance({ ...DEFAULT_APPEARANCE });
    setHasChanges(true);
    if (isUnityReady) {
      loadFullAppearance(DEFAULT_APPEARANCE);
    }
  }, [isUnityReady, loadFullAppearance]);

  const loadCharacterIntoForm = (character: Character) => {
    const parsed = parseAppearanceData(character.appearanceData);
    setName(character.name);
    setGender(character.gender);
    setCharacterClass(character.characterClass);
    setAppearance(parsed);
    setHasChanges(false);
    setActiveTab('preset');
    // Explicit full send: the load/identity effect doesn't key on appearance,
    // so character switches and cancel-reverts push their snapshot here. (If
    // Unity isn't ready yet, the effect covers it when isUnityReady flips.)
    if (isUnityReady) {
      loadFullAppearance(parsed);
    }
  };

  const handleSelectCharacter = (character: Character) => {
    setSelectedCharacter(character);
    loadCharacterIntoForm(character);
    setMode('edit');
    setSaveMessage(null);
  };

  const handleCreateNew = () => {
    setSelectedCharacter(null);
    // Use user's profile data as defaults
    setName(user?.displayName || '');
    setGender(userGenderToCharacterGender(user?.gender));
    setCharacterClass(user?.characterClass || 'tank');
    setAppearance(DEFAULT_APPEARANCE);
    setHasChanges(false);
    setActiveTab('preset');
    setMode('create');
    setSaveMessage(null);
    // Explicit full send (see loadCharacterIntoForm): covers clicking "new"
    // again while already in create mode, where no identity dep changes.
    if (isUnityReady) {
      loadFullAppearance(DEFAULT_APPEARANCE);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setSaveMessage({ type: 'error', text: 'Please enter a character name' });
      return;
    }

    if (name.length < 3) {
      setSaveMessage({ type: 'error', text: 'Name must be at least 3 characters' });
      return;
    }

    if (name.length > 20) {
      setSaveMessage({ type: 'error', text: 'Name cannot exceed 20 characters' });
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    // Whether this is the user's first-ever character (drives funnel_first_session_completed).
    const isFirstTime = mode === 'create' && characters.length === 0;

    try {
      if (mode === 'create') {
        const newCharacter = await createCharacter(name, characterClass, gender);
        if (newCharacter) {
          // Update appearance
          await updateCharacter(newCharacter.id, {
            appearanceData: serializeAppearanceData(appearance),
          });
          setSelectedCharacter(newCharacter);
          setMode('edit');
          setHasChanges(false);
          setSaveMessage({ type: 'success', text: 'Character created!' });
          emitSaveSucceeded(isFirstTime);
        } else {
          setSaveMessage({ type: 'error', text: contextError || 'Failed to create character' });
        }
      } else if (mode === 'edit' && selectedCharacter) {
        // B9: characterClass is intentionally NOT sent — the backend drops it.
        const updated = await updateCharacter(selectedCharacter.id, {
          name,
          gender,
          appearanceData: serializeAppearanceData(appearance),
        });
        if (updated) {
          setSelectedCharacter(updated);
          setHasChanges(false);
          // Also update user profile with character class
          await updateProfile({ characterClass, gender });
          setSaveMessage({ type: 'success', text: 'Character saved!' });
          emitSaveSucceeded(false);
        } else {
          setSaveMessage({ type: 'error', text: contextError || 'Failed to save character' });
        }
      }
    } catch {
      setSaveMessage({ type: 'error', text: 'An error occurred while saving' });
    }

    setIsSaving(false);
  };

  // US-5 save-step continuity + US-6 AC-4 first-session completion, on a server-confirmed save.
  const emitSaveSucceeded = (isFirstTime: boolean) => {
    const hadToken = hasLiveToken();
    emitTelemetry({
      event: hadToken ? 'session_continuity_ok' : 'session_continuity_broken',
      roomId: null, playerId: user?.id ?? null, correlationId: funnelCidRef, ts: nowMs(),
      payload: { step: 'save', hadToken },
    });

    // "Completion" = first successful save (plan OQ-5). Guard against re-emitting per user.
    const firstKey = user?.id ? `gamedate_first_session_completed_${user.id}` : null;
    const alreadyCompleted = firstKey ? localStorage.getItem(firstKey) === 'true' : false;
    if (isFirstTime && !alreadyCompleted) {
      if (firstKey) localStorage.setItem(firstKey, 'true');
      emitTelemetry({
        event: 'funnel_first_session_completed',
        roomId: null, playerId: user?.id ?? null, correlationId: funnelCidRef, ts: nowMs(),
        payload: { isFirstTime: true },
      });
    }
  };

  const handleDelete = async () => {
    if (!selectedCharacter) return;

    if (!confirm(`Delete ${selectedCharacter.name}? This cannot be undone.`)) {
      return;
    }

    setIsSaving(true);
    const success = await deleteCharacter(selectedCharacter.id);
    setIsSaving(false);

    if (success) {
      setSelectedCharacter(null);
      setMode('select');
      setSaveMessage({ type: 'success', text: 'Character deleted' });
    } else {
      setSaveMessage({ type: 'error', text: 'Failed to delete character' });
    }
  };

  const handleCancel = () => {
    if (hasChanges && !confirm('Discard unsaved changes?')) {
      return;
    }

    if (selectedCharacter) {
      loadCharacterIntoForm(selectedCharacter);
    } else if (activeCharacter) {
      setSelectedCharacter(activeCharacter);
      loadCharacterIntoForm(activeCharacter);
      setMode('edit');
    } else {
      setMode('select');
    }
    setHasChanges(false);
    setSaveMessage(null);
  };

  const renderOutfitGrid = () => (
    <PartImageGrid
      label="Outfit Preset"
      slot="outfit"
      value={appearance.outfitIndex}
      count={OUTFIT_COUNT}
      startIndex={1}
      tileLabels={OUTFIT_LABELS}
      large
      onChange={(value) => handleAppearanceChange({ outfitIndex: value })}
    />
  );

  return (
    <div className="character-editor-inline">
      <CharacterSlotSelector
        characters={characters}
        selectedCharacterId={selectedCharacter?.id || null}
        onSelectCharacter={handleSelectCharacter}
        onCreateNew={handleCreateNew}
      />

      {(mode === 'create' || mode === 'edit') && (
        <div className="editor-content">
          <div className="preview-panel">
            <div
              className="unity-container"
              onPointerDown={handlePreviewPointerDown}
              onPointerMove={handlePreviewPointerMove}
              onPointerUp={handlePreviewPointerEnd}
              onPointerCancel={handlePreviewPointerEnd}
            >
              {!isUnityLoaded && (
                <div className="loading-overlay">
                  <div className="loading-bar">
                    <div
                      className="loading-progress"
                      style={{ width: `${loadingProgression * 100}%` }}
                    />
                  </div>
                  <span className="loading-text">
                    Loading preview... {Math.round(loadingProgression * 100)}%
                  </span>
                </div>
              )}
              <Unity
                unityProvider={unityProvider}
                devicePixelRatio={devicePixelRatio}
                className="unity-canvas"
                style={{
                  width: '100%',
                  height: '100%',
                  visibility: isUnityLoaded ? 'visible' : 'hidden',
                }}
              />
            </div>
            <p className="preview-hint">Drag to rotate</p>
            <div className="animation-controls">
              {['idle', 'walk', 'run', 'wave', 'dance'].map((anim) => (
                <button
                  key={anim}
                  type="button"
                  className={`animation-btn ${currentAnimation === anim ? 'active' : ''}`}
                  onClick={() => handleAnimationChange(anim)}
                >
                  <span className="animation-emoji" aria-hidden="true">
                    {ANIMATION_EMOJI[anim]}
                  </span>
                  <span className="animation-label">{anim}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="controls-panel">
            <div className="editor-tabs" role="tablist">
              {EDITOR_TABS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === id}
                  className={`editor-tab ${activeTab === id ? 'active' : ''}`}
                  onClick={() => setActiveTab(id)}
                >
                  <Icon size={17} />
                  <span>{label}</span>
                </button>
              ))}
            </div>

            <div className="tab-panel">
              {activeTab === 'preset' && (
                <div className="tab-section">
                  <div className="form-group">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setHasChanges(true);
                      }}
                      placeholder="Enter character name"
                      maxLength={20}
                    />
                  </div>

                  <GenderSelector selectedGender={gender} onChange={handleGenderChange} />

                  {renderOutfitGrid()}

                  <div className="preset-actions">
                    <button type="button" className="btn-secondary btn-with-icon" onClick={handleRandomize}>
                      <DiceIcon size={16} />
                      <span>Randomize</span>
                    </button>
                    <button type="button" className="btn-secondary btn-with-icon" onClick={handleResetAppearance}>
                      <ResetIcon size={15} />
                      <span>Reset</span>
                    </button>
                  </div>
                </div>
              )}

              {activeTab === 'body' && (
                <div className="tab-section">
                  {BODY_GROUPS.map(({ title, Icon, keys }) => (
                    <div key={title} className="morph-group">
                      <div className="morph-group-header">
                        <Icon size={15} />
                        <h5 className="morph-group-title">{title}</h5>
                        <span className="morph-group-rule" />
                      </div>
                      <div className="morph-grid">
                        {keys.map((key) => {
                          const spec = MORPH_SPECS.find((s) => s.key === key);
                          if (!spec) return null;
                          return (
                            <MorphSlider
                              key={spec.key}
                              spec={spec}
                              value={appearance[spec.key]}
                              onChange={(value) => handleMorphChange(spec.key, value)}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'face' && (
                <div className="tab-section">
                  <PartImageGrid
                    label="Head"
                    slot="head"
                    value={appearance.headIndex}
                    count={PART_CATALOG.head}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('head', value)}
                  />
                  <PartImageGrid
                    label="Nose"
                    slot="nose"
                    value={appearance.noseIndex}
                    count={PART_CATALOG.nose}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('nose', value)}
                  />
                  <PartImageGrid
                    label="Ears"
                    slot="ear"
                    value={appearance.earIndex}
                    count={PART_CATALOG.ear}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('ear', value)}
                  />
                  <PartImageGrid
                    label="Eyebrows"
                    slot="eyebrow"
                    value={appearance.eyebrowIndex}
                    count={PART_CATALOG.eyebrow}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('eyebrow', value)}
                  />
                  <PartImageGrid
                    label="Teeth"
                    slot="teeth"
                    value={appearance.teethIndex}
                    count={PART_CATALOG.teeth}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('teeth', value)}
                  />
                  <PartImageGrid
                    label="Facial Hair"
                    slot="facialHair"
                    value={appearance.facialHairStyle}
                    count={PART_CATALOG.facialHair}
                    zeroLabel="None"
                    onChange={(value) => handlePartChange('facialHair', value)}
                  />
                  <ColorSwatchPicker
                    label="Skin"
                    presets={SKIN_PRESETS}
                    selectedColor={appearance.skinColor}
                    onChange={(color) => handleAppearanceChange({ skinColor: color })}
                  />
                  <ColorSwatchPicker
                    label="Eye Color"
                    presets={EYE_COLOR_PRESETS}
                    selectedColor={appearance.eyeColor}
                    onChange={(color) => handleAppearanceChange({ eyeColor: color })}
                  />
                </div>
              )}

              {activeTab === 'hair' && (
                <div className="tab-section">
                  <PartImageGrid
                    label="Hair Style"
                    slot="hair"
                    value={appearance.hairStyle}
                    count={PART_CATALOG.hair}
                    startIndex={0}
                    onChange={(value) => handlePartChange('hair', value)}
                  />
                  <ColorSwatchPicker
                    label="Hair Color"
                    presets={HAIR_COLOR_PRESETS}
                    selectedColor={appearance.hairColor}
                    onChange={(color) => handleAppearanceChange({ hairColor: color })}
                  />
                </div>
              )}

              {activeTab === 'outfit' && (
                <div className="tab-section">
                  {renderOutfitGrid()}

                  <h4 className="appearance-title appearance-title-iconed">
                    <PiecesIcon size={14} />
                    <span>Pieces</span>
                  </h4>
                  <PartImageGrid
                    label="Torso"
                    slot="torso"
                    value={appearance.torsoIndex}
                    count={PART_CATALOG.torso}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('torso', value)}
                  />
                  <PartImageGrid
                    label="Hips"
                    slot="hips"
                    value={appearance.hipsIndex}
                    count={PART_CATALOG.hips}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('hips', value)}
                  />
                  <PartImageGrid
                    label="Legs"
                    slot="legs"
                    value={appearance.legsIndex}
                    count={PART_CATALOG.legs}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('legs', value)}
                  />
                  <PartImageGrid
                    label="Shoes"
                    slot="feet"
                    value={appearance.feetIndex}
                    count={PART_CATALOG.feet}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('feet', value)}
                  />
                  <PartImageGrid
                    label="Sleeves Upper"
                    slot="armUpper"
                    value={appearance.armUpperIndex}
                    count={PART_CATALOG.armUpper}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('armUpper', value)}
                  />
                  <PartImageGrid
                    label="Sleeves Lower"
                    slot="armLower"
                    value={appearance.armLowerIndex}
                    count={PART_CATALOG.armLower}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('armLower', value)}
                  />
                  <PartImageGrid
                    label="Hands"
                    slot="hands"
                    value={appearance.handsIndex}
                    count={PART_CATALOG.hands}
                    zeroLabel="Auto"
                    onChange={(value) => handlePartChange('hands', value)}
                  />
                </div>
              )}

              {activeTab === 'accessories' && (
                <div className="tab-section">
                  <PartImageGrid
                    label="Hat"
                    slot="hat"
                    value={appearance.hatIndex}
                    count={PART_CATALOG.hat}
                    zeroLabel="None"
                    onChange={(value) => handlePartChange('hat', value)}
                  />
                  <PartImageGrid
                    label="Glasses"
                    slot="glasses"
                    value={appearance.glassesIndex}
                    count={PART_CATALOG.glasses}
                    zeroLabel="None"
                    onChange={(value) => handlePartChange('glasses', value)}
                  />
                  {/* Backpack picker removed intentionally: ABAC parts render at the
                      face (placement defect) — backpacks are disabled product-wide. */}
                  <PartImageGrid
                    label="Shoulder Pads"
                    slot="shoulder"
                    value={appearance.shoulderIndex}
                    count={PART_CATALOG.shoulder}
                    zeroLabel="None"
                    onChange={(value) => handlePartChange('shoulder', value)}
                  />
                </div>
              )}

              {activeTab === 'gestures' && (
                <div className="tab-section">
                  <div className="gesture-picker-header">
                    <h4 className="appearance-title">
                      My Gestures ({(appearance.gestures ?? []).length}/{MAX_GESTURE_SLOTS})
                    </h4>
                    <button
                      type="button"
                      className="btn-secondary gesture-reset-btn"
                      onClick={handleGestureReset}
                    >
                      Reset
                    </button>
                  </div>
                  <p className="gesture-picker-hint">
                    Pick up to {MAX_GESTURE_SLOTS} gestures for your in-room emote bar
                    (hotkeys 1–{MAX_GESTURE_SLOTS}). Tap any gesture to preview it on your
                    character.
                  </p>
                  {GESTURE_CATEGORY_LABELS.map(({ id: categoryId, label }) => {
                    const items = PICKABLE_GESTURES.filter((g) => g.category === categoryId);
                    if (items.length === 0) return null;
                    return (
                      <div key={categoryId} className="gesture-category">
                        <h5 className="gesture-category-title">{label}</h5>
                        <div className="gesture-grid">
                          {items.map((g) => {
                            const slot = (appearance.gestures ?? []).indexOf(g.id);
                            return (
                              <button
                                key={g.id}
                                type="button"
                                className={`gesture-btn ${slot >= 0 ? 'selected' : ''}`}
                                onClick={() => handleGestureToggle(g.id)}
                                title={g.label}
                              >
                                {slot >= 0 && (
                                  <span className="gesture-slot-badge">{slot + 1}</span>
                                )}
                                <span className="gesture-emoji" aria-hidden="true">
                                  {g.emoji}
                                </span>
                                <span className="gesture-label">{g.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {mode === 'select' && characters.length === 0 && (
        <div className="empty-state">
          <p>No characters yet. Create your first character!</p>
          <button type="button" className="btn-primary" onClick={handleCreateNew}>
            Create Character
          </button>
        </div>
      )}

      {saveMessage && (
        <div className={`save-message ${saveMessage.type}`}>
          {saveMessage.type === 'success' ? <CheckIcon size={15} /> : <AlertIcon size={15} />}
          <span>{saveMessage.text}</span>
        </div>
      )}

      {(mode === 'create' || mode === 'edit') && (
        <div className="editor-actions">
          {mode === 'edit' && selectedCharacter && (
            <button
              type="button"
              className="btn-danger btn-with-icon"
              onClick={handleDelete}
              disabled={isSaving}
            >
              <TrashIcon size={15} />
              <span>Delete</span>
            </button>
          )}
          <button
            type="button"
            className="btn-secondary btn-with-icon"
            onClick={handleCancel}
            disabled={isSaving}
          >
            <BackIcon size={15} />
            <span>{hasChanges ? 'Cancel' : 'Back'}</span>
          </button>
          <button
            type="button"
            className="btn-primary btn-with-icon"
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            <CheckIcon size={15} />
            <span>{isSaving ? 'Saving...' : mode === 'create' ? 'Create Character' : 'Save Changes'}</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default CharacterEditorInline;
